import { useEffect, useMemo, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import {
  buildFiltersQuery,
  getFilterParamNames,
  type UrlFilterCodecs,
} from "@/lib/urlFilters.util";
import { useDebounce } from "@/hooks/useDebounce";

export const URL_SEARCH_DEBOUNCE_MS = 300;

export function useUrlSyncedFilters<S extends object>(
  filters: S,
  codecs: UrlFilterCodecs<S>,
): void {
  const [, setSearchParams] = useSearchParams();

  // Comparing the query *string* is what keeps the URL from being rewritten on
  // every keystroke: the memo recomputes, but the effect only fires when a
  // filter actually changed.
  const ownedQuery = useMemo(() => buildFiltersQuery(filters, codecs), [filters, codecs]);
  const ownedParams = useMemo(() => getFilterParamNames(codecs), [codecs]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        ownedParams.forEach((param) => next.delete(param));
        new URLSearchParams(ownedQuery).forEach((value, key) => next.set(key, value));
        return next;
      },
      { replace: true },
    );
  }, [ownedQuery, ownedParams, setSearchParams]);
}

export function useUrlFiltersReducer<S extends { search: string }, A>(
  reducer: (state: S, action: A) => S,
  codecs: UrlFilterCodecs<S>,
  getInitialState: (searchParams: URLSearchParams) => S,
) {
  const [searchParams] = useSearchParams();
  const [filters, dispatch] = useReducer(reducer, searchParams, getInitialState);
  const debouncedSearch = useDebounce(filters.search, URL_SEARCH_DEBOUNCE_MS);

  const syncedFilters = useMemo(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch],
  );
  useUrlSyncedFilters(syncedFilters, codecs);

  return { filters, debouncedSearch, dispatch };
}
